import { delay } from 'redux-saga';
import {
    takeLatest, put, take, call, race, select
} from 'redux-saga/effects';
import {
    RETRIEVED_INITIATE_CONFIG_DATA,
    REQUEST_INITIATE_CONFIG_ERROR,
    fetchInitialPaymentConfigData,
    placeOrder,
    ORDER_SUCCESS,
    ORDER_FAILED,
    PAYMENT_FAILED
} from 'capi/redux';

import { List as IList, Map as IMap } from 'immutable';
import { logError, logWarning, reportPageLoadError } from '../utils/errorUtils';
import {
    paymentMethodDataHandlers,
    
    PROCESS_PAYMENT,
    PROCESS_INITIATE_CONFIG,
    
    initiateConfigRequestSuccess,
    initiateConfigRequestFailed,
    processInitiateConfig,
    startSendOrder,
    resetPaymentStatus,
    paymentSuccess,
    paymentFailed,
    paymentComplete,
    paymentFailedPromo,
    updatePaymentWidgetLoadStartTime,
    trackPaymentWidgetLoadStatus
} from './paymentModule';
import {
    showAppError,
    APP_ERROR_TYPES,
    clearCompletedSteps
} from '../routing/routingModule';
import {
    sanitizePaymentData,
    orderSerializer,
    orderResponseSerializer,
    initiateConfigSerializer
} from './capiSerializer';
import {
    getOrderMetaData, getPaymentGrandTotal as getGrandTotal, getLastAuthReasonCode,
    getLoyaltyAppliedAmount
} from './paymentSelectors';
import { resetCardSelection } from '../landingPage/landingPageModule';
import { flushProductDetails, updateItemInCart, setOrderNumber } from '../productCartHandler/productCartModule';
import { resetIovation } from '../iovation/iovationModule';
import { getIntlLocale, getIntlCurrencyCode } from '../intl/intlSelectors';
import { getStorefrontSessionKey } from '../session/sessionSelectors';
import { handleExpiredSession, detectExpiredSession } from '../auth/sessionExpiryRetry';
import {
    resetPromo, clearRejectedPromos, setPromoStatus, RECEIVED_PROMO_REWARDS
} from '../promo/promoModule';
import {
    getBonusCards,
    getTransformedPromoItems,
    getPromoLineItems,
    getActivePromoCode,
    getCustomRewardCardRecipients,
    getShippingPromoData,
    getPromoRewardCards
} from '../promo/promoSelectors';
import { getAccessoriesPromoData, getDiscountRewardAmount } from '../promo/sharedCartPromoSelectors';
import { loadingAction } from '../loader/loaderModule';
import { resetLoyalty } from '../loyalty/loyaltyModule';
import {
    getSubTotal,
    getActivationFeeTotal,
    getActivationFeeCount,
    getPaymentEGiftCards,
    getItemRecipients,
    getAllPrintAtHomeCards,
    getPrintAtHomeCardQuantity,
    getDigitalCardQuantityExcludingPrintAtHome,
    getTotalCardQuantity, getConfirmationEGiftCards,
    getPaymentPhysicalGiftCards,
    getPhysicalCardQuantity,
    getActivationFeeforDigitalAndPhysical,
    getItemAccessoriesGrandTotalCost,
    getItemsAccessoryCount, getGreetingCount, getAllActiveCards,
    getOrderNumber
} from '../productCartHandler/productCartSelectors';
import { getIsBuyNowFlow } from '../routing/routingSelectors';
import {
    getBrandCode,
    getEgcRecommendedDenoms,
    getGreetingsPrice,
    getPlasticRecommendedDenoms,
    getUYOPlasticPhotoSupported
} from '../brand/brandSelectors';
import { getConsumerFees } from '../fees/consumerFeeSelectors';
import { flushShippingDetails } from '../shipping/shippingModule';
import { getShippingDisplayName, getShippingSummary } from '../shipping/shippingSelectors';
import { getSelectedFaceplateImageURL } from '../landingPage/landingPageSelectors';
import { resetCardholderAgreementAcceptance } from '../payment_v2/consentModule';
import { removeCpfNumber, setIsCartEdited } from '../cart/cartModule';
import { getCPFNumber } from '../cart/cartSelectors';
import { ENABLE_DRAFT_ORDER_NUMBER_DECOUPLING } from './checkoutIdentifierConfig';

export const defaultFailureResponse = {
    status: 402,
    statusText: 'Payment Required',
    data: {
        errorCode: 'PAYMENT_REQUIRED',
        fieldErrors: []
    }
};

export const parseErrorResponse = response => ({
    httpCode: response.status,
    httpMessage: response.statusText,
    capiCode: response.data.errorCode,
    fieldErrors: response.data.fieldErrors
});

export const parseErrorResponseForSegment = response => ({
    httpCode: response.status,
    httpMessage: response.statusText || JSON.stringify(response.data)
});


const PROMO_REWARDS_WAIT_TIMEOUT_MS = 5000;




const INITIATE_CONFIG_WAIT_TIMEOUT_MS = 30000;

export const extractCartItemTokensFromInitiateConfigResponse = (responseData) => {
    const cartItems = responseData?.cartResponse?.cart_items || [];
    if (cartItems.length > 0) {
        const getTokenFromCartItem = item => (
            item?.cartItemToken
            || item?.data?.cartItemToken
            || item?.hashed_id
            || ''
        );
        const isDigital = item => item?.delivery_option === 'DIGITAL';
        const digitalTokens = cartItems.filter(isDigital).map(getTokenFromCartItem).filter(Boolean);
        const physicalTokens = cartItems.filter(item => !isDigital(item)).map(getTokenFromCartItem).filter(Boolean);
        return { digitalTokens, physicalTokens };
    }

    const checkoutData = responseData?.checkout_request_data || responseData?.checkout || {};
    const egiftCards = checkoutData?.egiftCards || [];
    const shippingGroups = checkoutData?.shippingGroups || [];
    const digitalTokens = egiftCards.map(card => card?.cartItemToken || '').filter(Boolean);
    const physicalTokens = shippingGroups.reduce((tokens, group) => {
        const certs = group?.certs || group?.items || [];
        return tokens.concat(certs.map(cert => cert?.cartItemToken || '').filter(Boolean));
    }, []);

    return { digitalTokens, physicalTokens };
};

export function* syncCartItemTokensFromInitiateConfig(responseData) {
    const { digitalTokens, physicalTokens } = extractCartItemTokensFromInitiateConfigResponse(responseData);
    const eGiftCards = yield select(getPaymentEGiftCards);
    const physicalGiftCards = yield select(getPaymentPhysicalGiftCards);

    const digitalCount = Math.min(eGiftCards.size, digitalTokens.length);
    for (let i = 0; i < digitalCount; i += 1) {
        const item = eGiftCards.get(i);
        const token = digitalTokens[i];
        if (token) {
            yield put(updateItemInCart(item.set('cartItemToken', token)));
        }
    }

    const physicalCount = Math.min(physicalGiftCards.size, physicalTokens.length);
    for (let i = 0; i < physicalCount; i += 1) {
        const item = physicalGiftCards.get(i);
        const token = physicalTokens[i];
        if (token) {
            yield put(updateItemInCart(item.set('cartItemToken', token)));
        }
    }
}

export function* initiateConfigSaga(action) {
    
    yield put(updatePaymentWidgetLoadStartTime(Date.now()));
    const {
        data: {
            isEdited, retryCheckoutOnConflict = false, draftOrderNumber, token
        },
        paymentTrackingNew
    } = action;
    const isBuyNow = yield select(getIsBuyNowFlow);
    const activePromoCode = yield select(getActivePromoCode);
    
    if (isBuyNow && activePromoCode) {
        const { timeout } = yield race({
            received: take(RECEIVED_PROMO_REWARDS),
            timeout: delay(PROMO_REWARDS_WAIT_TIMEOUT_MS)
        });
        if (timeout) {
            logWarning('Timeout waiting for promo rewards before initiate-config');
        }
    }
    const eGiftCards = yield select(getPaymentEGiftCards);
    const physicalGiftCards = yield select(getPaymentPhysicalGiftCards);
    const orderMetaData = yield select(getOrderMetaData);
    const brandDefaultLocale = yield select(getIntlLocale);
    const fees = yield select(getConsumerFees);
    const uyoImageURL = yield select(getSelectedFaceplateImageURL);
    const shippingPromoData = yield select(getShippingPromoData);
    const promoRewards = yield select(getPromoRewardCards);
    const promoAccessoryData = yield select(getAccessoriesPromoData);
    const cpfNumber = yield select(getCPFNumber);
    const uyoPlasticPhotoSupported = yield select(getUYOPlasticPhotoSupported);

    const countryCode = (brandDefaultLocale).split('-')[1].toUpperCase();

    if (!orderMetaData.get('currencyCode')) {
        reportPageLoadError(
            'Error on initiate-config, currency code is empty or null',
            'Error on initiate-config, currency code is empty or null'
        );
    }

    
    const data = yield call(
        initiateConfigSerializer,
        eGiftCards,
        orderMetaData,
        {
            countryCode,
            isEdited,
            uyoImageURL,
            retryCheckoutOnConflict,
            draftOrderNumber,
            token
        },
        fees,
        physicalGiftCards,
        shippingPromoData,
        promoRewards,
        cpfNumber,
        promoAccessoryData,
        uyoPlasticPhotoSupported
    );
    
    yield put(fetchInitialPaymentConfigData(data.toJS()));
    
    
    
    const { result, timeout } = yield race({
        result: take([
            RETRIEVED_INITIATE_CONFIG_DATA, REQUEST_INITIATE_CONFIG_ERROR
        ]),
        timeout: delay(INITIATE_CONFIG_WAIT_TIMEOUT_MS)
    });

    if (timeout) {
        logError('Timeout waiting for initiate config response from CAPI');
        reportPageLoadError(
            'Timeout waiting for initiate config response',
            'No initiate-config success or error action within timeout'
        );
        yield put(initiateConfigRequestFailed(new Error('Initiate config request timed out')));
        if (paymentTrackingNew) {
            yield put(trackPaymentWidgetLoadStatus({
                wl_status: 'Fail',
                remarks: 'Initiate config request timed out'
            }));
        }
        return;
    }

    switch (result.type) {
        case RETRIEVED_INITIATE_CONFIG_DATA: {
            yield call(syncCartItemTokensFromInitiateConfig, result.data);
            
            
            
            if (result.data && result.data.draftOrderNumber) {
                yield put(setOrderNumber(result.data.draftOrderNumber));
            }
            yield put(initiateConfigRequestSuccess(result.data));
            break;
        }
        case REQUEST_INITIATE_CONFIG_ERROR: {
            
            
            
            
            
            
            
            
            
            
            
            const handled = yield* handleExpiredSession(result.error);
            if (handled && !action.data.sessionRetried) {
                yield put(processInitiateConfig({ ...action.data, sessionRetried: true }));
                break;
            }
            
            logError('Error on initiate config call. Capi-js returned a request failure');
            reportPageLoadError('Error on initiate config call', 'Error on initiate config call. Capi-js returned a request failure');
            
            yield put(initiateConfigRequestFailed(result.error));

            if (paymentTrackingNew) {
                yield put(trackPaymentWidgetLoadStatus({
                    wl_status: 'Fail',
                    remarks: result.error
                }));
            }
            break;
        }
        default:
            break;
    }
}

export function* initiateConfigWatcherSaga() {
    yield takeLatest(PROCESS_INITIATE_CONFIG, initiateConfigSaga);
}


export function* gatewayTimeoutSaga() {
        yield put(showAppError(APP_ERROR_TYPES.NO_RETRY));
        yield put(paymentComplete());
    yield put(resetCardSelection());
    yield put(flushProductDetails());
    yield put(flushShippingDetails());
    yield put(resetIovation());
    yield put(clearCompletedSteps());
}

export function* orderingSaga(payment, paymentTrackingNew, intl) {
    const eGiftCards = yield select(getPaymentEGiftCards);
    const subTotal = parseFloat((yield select(getSubTotal)).toFixed(2));
    const grandTotal = yield select(getGrandTotal);
    const activationFeeTotal = yield select(getActivationFeeTotal);
    const activationFeeQuantity = yield select(getActivationFeeCount);
    const ItemRecipients = yield select(getItemRecipients);
    const promoCardRewards = yield select(getTransformedPromoItems);
    const getPromoItems = yield select(getPromoLineItems);
    const customRewardCardRecipients = yield select(getCustomRewardCardRecipients);
    const getDigitalCardsCount = yield select(getDigitalCardQuantityExcludingPrintAtHome);
    const getTotalQuantity = yield select(getTotalCardQuantity);
    const sessionKey = yield select(getStorefrontSessionKey);
    const egcRecommendedDenoms = yield select(getEgcRecommendedDenoms);
    const plasticRecommendedDenoms = yield select(getPlasticRecommendedDenoms);
    const fees = yield select(getConsumerFees);
    const physicalGiftCards = yield select(getPaymentPhysicalGiftCards);
    const shippingPromoData = yield select(getShippingPromoData);
    const shippingDisplayTexts = yield select(getShippingDisplayName, intl);
    const cpfNumber = yield select(getCPFNumber);
    const uyoPlasticPhotoSupported = yield select(getUYOPlasticPhotoSupported);
    
    
    
    const draftOrderNumber = yield select(getOrderNumber);

    const orderMetaData = yield select(getOrderMetaData);
    const order = yield call(orderSerializer,
        payment,
        orderMetaData,
        eGiftCards,
        physicalGiftCards,
        shippingPromoData,
        fees,
        customRewardCardRecipients,
        cpfNumber,
        uyoPlasticPhotoSupported,
        draftOrderNumber);
    
    yield put(placeOrder(order.toJS()));

    
    const result = yield take([
        ORDER_SUCCESS, ORDER_FAILED, PAYMENT_FAILED
    ]);

            payment = sanitizePaymentData(payment);

    const totalAmount = orderMetaData.get('amount').toFixed(2);
    const currencyCode = orderMetaData.get('currencyCode');

    switch (result.type) {
        case ORDER_FAILED: {
            logWarning(result.error);
            reportPageLoadError('Warning inside ORDER_FAILED', result.error);
            const res = typeof result.error.response !== 'undefined'
                ? result.error.response : defaultFailureResponse;
            const parsedResponse = parseErrorResponse(res);
            const parsedErrorForSegment = parseErrorResponseForSegment(res);
            
            
            
            
            
            
            
            if (yield* handleExpiredSession(result.error)) {
                yield put(loadingAction({ isLoading: false }));
                break;
            }
            if (res.status === 403 || res.status === 400) {
                yield put(paymentFailedPromo(
                    parsedResponse,
                    {
                        error: parsedErrorForSegment,
                        eGiftCards,
                        totalAmount,
                        currencyCode
                    }
                ));
                yield put(setPromoStatus({ statusType: res.data.errorCode, promoCode: order.toJS().promoCode }));
                yield put(loadingAction({ isLoading: false }));
            } else if (res.status === 504) {
                logWarning('Order API responded with a order placement timeout');
                reportPageLoadError('Warning inside ORDER_FAILED, status 504', 'Order API responded with a order placement timeout');
                yield call(gatewayTimeoutSaga);
            } else {
                const failedCaseId = JSON.parse(res.config.data).paymentPlus.response.caseId;
                logWarning(`Order API failed for caseId (informational): ${failedCaseId}`);
                yield put(paymentFailed(
                    parsedResponse,
                    {
                        error: parsedErrorForSegment,
                        totalAmount,
                        currencyCode,
                        eGiftCards,
                        physicalGiftCards,
                        shippingDisplayTexts,
                        paymentMethod: payment.get('paymentMethod'),
                        grandTotal,
                        subTotal,
                        cashstarSessionKey: sessionKey,
                        orderMetaData,
                        egcRecommendedDenoms,
                        plasticRecommendedDenoms,
                        intl,
                        
                        
                        
                        
                        
                        orderNumber: ENABLE_DRAFT_ORDER_NUMBER_DECOUPLING ? draftOrderNumber : failedCaseId,
                        paymentTrackingNew
                    }
                ));
            }
            break;
        }
        case PAYMENT_FAILED: {
            const additionalSegmentData = {
                eGiftCards, physicalGiftCards, shippingDisplayTexts, totalAmount, currencyCode
            };
            yield call(paymentErrorHandler, result.error, payment, payment.get('paymentMethod'), additionalSegmentData);
            break;
        }
        case ORDER_SUCCESS: {
                        try {
                
                const isBuyNow = yield select(getIsBuyNowFlow);
                const orderData = yield call(orderResponseSerializer, result.data);
                const bonusCards = yield select(getBonusCards);
                const confirmationEGiftCards = yield select(getConfirmationEGiftCards);
                const paymentEGiftCards = yield select(getPaymentEGiftCards);
                const confirmationPrintAtHomeCards = !isBuyNow ? yield select(getAllPrintAtHomeCards) : IList();
                const getPrintAtHomeCardsCount = yield select(getPrintAtHomeCardQuantity);
                const confirmationPhysicalGiftCards = yield select(getPaymentPhysicalGiftCards);
                const getPhysicalCardCount = yield select(getPhysicalCardQuantity);
                const bonusCardsCopy = JSON.parse(JSON.stringify(bonusCards.toJS()));
                const shippingSummary = yield select(getShippingSummary);
                const activationFeeSummary = yield select(getActivationFeeforDigitalAndPhysical);
                const discountRewardAmount = yield select(getDiscountRewardAmount);
                const shippingDisplayName = yield select(getShippingDisplayName, intl);
                const accessoryTotalAmount = yield select(getItemAccessoriesGrandTotalCost);
                const accessoryCardCount = yield select(getItemsAccessoryCount);
                yield put(clearRejectedPromos());
                const totalGreetingsCount = yield select(getGreetingCount);
                const greetingPrice = yield select(getGreetingsPrice);
                const lastAuthReasonCode = yield select(getLastAuthReasonCode);
                const totalLoyalPoints = yield select(getLoyaltyAppliedAmount);
                
                
                const paymentResponse = order.getIn(['paymentPlus', 'response'], IMap());
                const splitPaymentItems = paymentResponse.get('payments');
                const paymentItems = splitPaymentItems ? splitPaymentItems.toJS() : [paymentResponse.toJS()];
                
                
                
                const activeCardDetails = (yield select(getAllActiveCards)).get(0, IMap({})).toJS();

                const orderDetails = {
                    orderData: orderData.toJS(),
                    eGiftCards: confirmationEGiftCards.toJS(),
                    amount: totalAmount,
                    currencyCode,
                    sessionKey,
                    bonusCards: bonusCardsCopy,
                    subTotalAmount: subTotal,
                    activFeeTotal: activationFeeTotal,
                    activFeeQuantity: activationFeeQuantity,
                    getActiveItemRecipients: ItemRecipients.toJS(),
                    promoCardRewards: promoCardRewards.toJS(),
                    PromoItems: getPromoItems.toJS(),
                    confirmationCustomPromoRecipients: customRewardCardRecipients.toJS(),
                    totalQuantity: getTotalQuantity,
                    printAtHomeCards: confirmationPrintAtHomeCards.toJS(),
                    printAtHomeCardsCount: getPrintAtHomeCardsCount,
                    digitalCardsCount: getDigitalCardsCount,
                    isBuyNowFlow: isBuyNow,
                    physicalGiftCards: confirmationPhysicalGiftCards.toJS(),
                    physicalCardsCount: getPhysicalCardCount,
                    shippingSummary,
                    activationFeeSummary,
                    totalDiscountRewardAmount: discountRewardAmount,
                    shippingDisplayName,
                    accessoryTotalAmount: parseFloat(accessoryTotalAmount.toFixed(2)),
                    accessoryCardCount,
                    greetingsCount: totalGreetingsCount,
                    greetingsPrice: greetingPrice,
                    lastAuthReasonCode,
                    totalLoyalPoints,
                    paymentItems,
                    confirmationActiveCardDetails: activeCardDetails
                };

                
                yield put(paymentSuccess(
                    orderDetails,
                    {
                        paymentMethod: payment.get('paymentMethod'),
                        grandTotal,
                        orderMetaData,
                        egcRecommendedDenoms,
                        plasticRecommendedDenoms,
                        shippingDisplayTexts,
                        paymentTrackingNew,
                        paymentEGiftCards
                    }
                ));
            } catch (e) {
                
                yield put(showAppError(APP_ERROR_TYPES.NO_RETRY));
                logError(`Order success handling failed: "${e}", bailing and showing the user a 500`);
                reportPageLoadError('Order success handling failed:', `Order success handling failed: "${e}", bailing and showing the user a 500`);
            } finally {
                                yield put(paymentComplete());
                yield put(resetCardSelection());
                yield put(flushProductDetails());
                yield put(flushShippingDetails());
                yield put(resetIovation());
                yield put(resetPromo());
                yield put(resetLoyalty());
                yield put(resetCardholderAgreementAcceptance());
                yield put(setIsCartEdited(false));
                yield put(removeCpfNumber());
            }
            break;
        }
        default:
            break;
    }
}

export function* paymentErrorHandler(error, payment, paymentMethod, additionalSegmentData = {}) {
    logWarning(`${paymentMethod} auth failed: ${error}`, payment);
    reportPageLoadError('Warning inside paymentErrorHandler', `${paymentMethod} auth failed: ${error}`, payment);
    const parsedResponse = parseErrorResponse(defaultFailureResponse);
    const parsedErrorForSegment = parseErrorResponseForSegment(defaultFailureResponse);
    yield put(paymentFailed(parsedResponse, { error: parsedErrorForSegment, ...additionalSegmentData }));
}

export function* placeOrderSaga() {
        while (true) {
                let {
                        paymentMethod, paymentData, paymentTrackingNew, intl
        } = yield take(PROCESS_PAYMENT);
        
        
        
        
        
        
        if (yield* detectExpiredSession()) {
            yield put(resetPaymentStatus());
            yield put(loadingAction({ isLoading: false }));
            
            continue;
        }
        
        const eGiftCards = yield select(getPaymentEGiftCards);
        const physicalGiftCards = yield select(getPaymentPhysicalGiftCards);
        const shippingDisplayTexts = yield select(getShippingDisplayName, intl);
        const totalAmount = (yield select(getGrandTotal)).toFixed(2);
        const currencyCode = yield select(getIntlCurrencyCode);
        const grandTotal = yield select(getGrandTotal);
        const subTotal = yield select(getSubTotal);
        const brandCode = yield select(getBrandCode);
        const language = yield select(getIntlLocale);
        const egcRecommendedDenoms = yield select(getEgcRecommendedDenoms);
        const plasticRecommendedDenoms = yield select(getPlasticRecommendedDenoms);
        const promoCode = yield select(getActivePromoCode);
        const uyoImageURL = yield select(getSelectedFaceplateImageURL);
        
        
        const draftOrderNumber = yield select(getOrderNumber);

        const placeOrdeSegmentInputData = {
            eGiftCards,
            orderNumber: ENABLE_DRAFT_ORDER_NUMBER_DECOUPLING
                ? draftOrderNumber
                : paymentData.paymentPlusAuthResponse.caseId,
            grandTotal,
            subTotal: parseInt(subTotal.toFixed(2), 10),
            currencyCode,
            paymentMethod,
            language,
            brandCode,
            egcRecommendedDenoms,
            plasticRecommendedDenoms,
            intl,
            promoCode,
            uyoImageURL,
            physicalGiftCards,
            shippingDisplayTexts
        };

        
        yield put(startSendOrder(placeOrdeSegmentInputData, paymentTrackingNew));

        try {
            const paymentMethodDataHandler = paymentMethodDataHandlers.get(paymentMethod);
            let payment = yield call(paymentMethodDataHandler, paymentData);

            
            if (payment.get('approved')) {
                                const ORDERING_TIMEOUT_THRESHOLD = 30; 
                const { timeout, response } = yield race({
                    timeout: delay(ORDERING_TIMEOUT_THRESHOLD * 1000),
                    response: call(orderingSaga, payment, paymentTrackingNew, intl)
                });

                if (timeout && !response) {
                    logWarning('Order placement timeout: No response from order API');
                    reportPageLoadError('Warning inside PlaceOrderSaga', 'Order placement timeout: No response from order API');
                    yield call(gatewayTimeoutSaga);
                }
            } else {
                const error = 'Not approved from storefront';
                payment = sanitizePaymentData(payment);
                const additionalSegmentData = {
                    eGiftCards, physicalGiftCards, totalAmount, currencyCode, shippingDisplayTexts
                };
                yield call(paymentErrorHandler, error, payment, paymentMethod, additionalSegmentData);
            }
        } catch (e) {
                        paymentData = sanitizePaymentData(paymentData);
            const additionalSegmentData = {
                eGiftCards, physicalGiftCards, totalAmount, currencyCode, shippingDisplayTexts
            };
            yield call(paymentErrorHandler, e, paymentData, paymentMethod, additionalSegmentData);
        }
    }
}

export default placeOrderSaga;
